import React from 'react';
import {useScreenSizeContext} from "../../providers/ScreenSizeProvider";

interface AddressFormCardProps {
    mobilePhone: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    country: string;
    zipCode: string;
    state: string;
    saveBillingInfo: boolean;
    setMobilePhone: (value: string) => void;
    setAddressLine1: (value: string) => void;
    setAddressLine2: (value: string) => void;
    setCity: (value: string) => void;
    setCountry: (value: string) => void;
    setZipCode: (value: string) => void;
    setState: (value: string) => void;
    setSaveBillingInfo: (value: boolean) => void;
    fieldErrors: { [key: string]: string };
    handleFieldChange: (field: string, value: string, additionalValue?: string, isBlur?: boolean) => void;
}

const AddressFormCard: React.FC<AddressFormCardProps> = ({
    mobilePhone,
    addressLine1,
    addressLine2,
    city,
    country,
    zipCode,
    state,
    saveBillingInfo,
    setMobilePhone,
    setAddressLine1,
    setAddressLine2,
    setCity,
    setCountry,
    setZipCode,
    setState,
    setSaveBillingInfo,
    fieldErrors,
    handleFieldChange
}) => {
    const { isMobile } = useScreenSizeContext();

    const handleMobilePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        // Ensure only numbers are input
        if (/^\d*$/.test(value)) {
            setMobilePhone(value);
            handleFieldChange('phone', value);
        }
    };

    const handleMobilePhoneBlur = () => {
        handleFieldChange('phone', mobilePhone, undefined, true);
    };

    const handleAddressLine1Change = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setAddressLine1(value);
        handleFieldChange('address', value);
    };

    const handleAddressLine1Blur = () => {
        handleFieldChange('address', addressLine1, undefined, true);
    };

    const handleAddressLine2Change = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAddressLine2(e.target.value);
    };

    const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setCity(value);
        handleFieldChange('city', value);
    };

    const handleCityBlur = () => {
        handleFieldChange('city', city, undefined, true);
    };

    const handleZipCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setZipCode(value);
        handleFieldChange('zipCode', value);
    };

    const handleZipCodeBlur = () => {
        handleFieldChange('zipCode', zipCode, undefined, true);
    };

    const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setCountry(e.target.value);
    };

    const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;
        setState(value);
        handleFieldChange('state', value);
    };

    return (
        <div className={isMobile ? '' : 'card'}>
            <div className='text-l'>Billing Address</div>
            <div className='card-item'>
                <div className='row'>
                    <div className='form-group'>
                        <input
                            type="text"
                            value={addressLine1}
                            onChange={handleAddressLine1Change}
                            onBlur={handleAddressLine1Blur}
                            placeholder="Address Line 1"
                            className={`input ${fieldErrors['address'] ? 'error' : ''}`}
                        />
                        {fieldErrors['address'] && <div className="error-message">{fieldErrors['address']}</div>}
                    </div>
                    <div className='gap-m' />
                    <div className='form-group'>
                        <input
                            type="text"
                            value={addressLine2}
                            onChange={handleAddressLine2Change}
                            placeholder="Address Line 2 (Optional)"
                            className="input"
                        />
                    </div>
                </div>
                <div className='row'>
                    <div className='form-group'>
                        <input
                            type="text"
                            value={city}
                            onChange={handleCityChange}
                            onBlur={handleCityBlur}
                            placeholder="City"
                            className={`input ${fieldErrors['city'] ? 'error' : ''}`}
                        />
                        {fieldErrors['city'] && <div className="error-message">{fieldErrors['city']}</div>}
                    </div>
                    <div className='gap-m' />
                    <div className='form-group'>
                        <input
                            type="text"
                            value={zipCode}
                            onChange={handleZipCodeChange}
                            onBlur={handleZipCodeBlur}
                            placeholder="Zip Code"
                            className={`input ${fieldErrors['zipCode'] ? 'error' : ''}`}
                        />
                        {fieldErrors['zipCode'] && <div className="error-message">{fieldErrors['zipCode']}</div>}
                    </div>
                </div>
                <div className='row'>
                    <div className='form-group'>
                        <select
                            value={country}
                            onChange={handleCountryChange}
                            className="input"
                            required
                        >
                            <option value="" disabled>Select Country</option>
                            <option value="US">United States</option>
                            <option value="CA">Canada</option>
                        </select>
                    </div>
                    <div className='gap-m' />
                    <div className='form-group'>
                        <select
                            value={state}
                            onChange={handleStateChange}
                            className={`input ${fieldErrors['state'] ? 'error' : ''}`}
                            required
                        >
                            <option value="" disabled>Select State/Province</option>
                            {country === "US" && (
                                <>
                                    <option value="AL">Alabama</option>
                                    <option value="AK">Alaska</option>
                                    <option value="AZ">Arizona</option>
                                    <option value="AR">Arkansas</option>
                                    <option value="CA">California</option>
                                    <option value="CO">Colorado</option>
                                    <option value="CT">Connecticut</option>
                                    <option value="DE">Delaware</option>
                                    <option value="FL">Florida</option>
                                    <option value="GA">Georgia</option>
                                    <option value="HI">Hawaii</option>
                                    <option value="ID">Idaho</option>
                                    <option value="IL">Illinois</option>
                                    <option value="IN">Indiana</option>
                                    <option value="IA">Iowa</option>
                                    <option value="KS">Kansas</option>
                                    <option value="KY">Kentucky</option>
                                    <option value="LA">Louisiana</option>
                                    <option value="ME">Maine</option>
                                    <option value="MD">Maryland</option>
                                    <option value="MA">Massachusetts</option>
                                    <option value="MI">Michigan</option>
                                    <option value="MN">Minnesota</option>
                                    <option value="MS">Mississippi</option>
                                    <option value="MO">Missouri</option>
                                    <option value="MT">Montana</option>
                                    <option value="NE">Nebraska</option>
                                    <option value="NV">Nevada</option>
                                    <option value="NH">New Hampshire</option>
                                    <option value="NJ">New Jersey</option>
                                    <option value="NM">New Mexico</option>
                                    <option value="NY">New York</option>
                                    <option value="NC">North Carolina</option>
                                    <option value="ND">North Dakota</option>
                                    <option value="OH">Ohio</option>
                                    <option value="OK">Oklahoma</option>
                                    <option value="OR">Oregon</option>
                                    <option value="PA">Pennsylvania</option>
                                    <option value="RI">Rhode Island</option>
                                    <option value="SC">South Carolina</option>
                                    <option value="SD">South Dakota</option>
                                    <option value="TN">Tennessee</option>
                                    <option value="TX">Texas</option>
                                    <option value="UT">Utah</option>
                                    <option value="VT">Vermont</option>
                                    <option value="VA">Virginia</option>
                                    <option value="WA">Washington</option>
                                    <option value="WV">West Virginia</option>
                                    <option value="WI">Wisconsin</option>
                                    <option value="WY">Wyoming</option>
                                </>
                            )}
                            {country === "CA" && (
                                <>
                                    <option value="AB">Alberta</option>
                                    <option value="BC">British Columbia</option>
                                    <option value="MB">Manitoba</option>
                                    <option value="NB">New Brunswick</option>
                                    <option value="NL">Newfoundland and Labrador</option>
                                    <option value="NT">Northwest Territories</option>
                                    <option value="NS">Nova Scotia</option>
                                    <option value="NU">Nunavut</option>
                                    <option value="ON">Ontario</option>
                                    <option value="PE">Prince Edward Island</option>
                                    <option value="QC">Quebec</option>
                                    <option value="SK">Saskatchewan</option>
                                    <option value="YT">Yukon</option>
                                </>
                            )}
                        </select>
                        {fieldErrors['state'] && <div className="error-message">{fieldErrors['state']}</div>}
                    </div>
                </div>
                <div className='row'>
                    <div className='form-group'>
                        <input
                            type="tel"
                            value={mobilePhone}
                            onChange={handleMobilePhoneChange}
                            onBlur={handleMobilePhoneBlur}
                            placeholder="Mobile Phone"
                            className={`input ${fieldErrors['phone'] ? 'error' : ''}`}
                        />
                        {fieldErrors['phone'] && <div className="error-message">{fieldErrors['phone']}</div>}
                    </div>
                </div>
            </div>
            <div className='card-item'>
                <div className='row'>
                            <input
                                type="checkbox"
                                checked={saveBillingInfo}
                        onChange={() => setSaveBillingInfo(!saveBillingInfo)}
                        className='checkbox'
                            />
                    <div className='text-xs'>Save info to my account for future orders and consignments</div>
                </div>
            </div>
        </div>
    )
}

export default AddressFormCard;
